import appLogger from "@public/log/monitor";
import { zoomImg } from '@q/solib-utils';
import { makeAutoObservable } from "mobx";

// 处理图片尺寸限制和等比例缩放
export const adjustImageSize = (
  width: number,
  height: number
): { width: number; height: number; scale: number } => {
  const MAX_SIZE = 2048;
  const MIN_SIZE = 64;
  let adjustedWidth = width;
  let adjustedHeight = height;
  const ratio = width / height;
  let scale = 1;

  // 处理超过最大尺寸的情况
  if (width >= height) {
    // 宽边较长
    if (width > MAX_SIZE) {
      scale = MAX_SIZE / width;
      adjustedWidth = MAX_SIZE;
      adjustedHeight = Math.round(MAX_SIZE / ratio);
    }
  } else {
    // 高边较长
    if (height > MAX_SIZE) {
      scale = MAX_SIZE / height;
      adjustedHeight = MAX_SIZE;
      adjustedWidth = Math.round(MAX_SIZE * ratio);
    }
  }

  // 处理小于最小尺寸的情况
  if (width >= height) {
    // 宽边较长，检查高边
    if (adjustedHeight < MIN_SIZE) {
      scale = MIN_SIZE / height;
      adjustedHeight = MIN_SIZE;
      adjustedWidth = Math.round(MIN_SIZE * ratio);
    }
  } else {
    // 高边较长，检查宽边
    if (adjustedWidth < MIN_SIZE) {
      scale = MIN_SIZE / width;
      adjustedWidth = MIN_SIZE;
      adjustedHeight = Math.round(MIN_SIZE / ratio);
    }
  }

  return {
    width: adjustedWidth,
    height: adjustedHeight,
    scale: scale
  };
};
class Store {
  imgUrl = '';
  prompt = '';
  currentRatio = 16 / 9;
  outerSize = {
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    realWidth: 0,
    realHeight: 0,
  };
  innerSize = {
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    realWidth: 0,
    realHeight: 0,
  };
  originSize = {
    width: 0,
    height: 0,
    wrapWidth: 0,
    wrapHeight: 0,
  };

  outerTranslate = { left: 0, top: 0, right: 0, bottom: 0 };
  innerTranslate = { left: 0, top: 0, right: 0, bottom: 0 };

  constructor() {
    makeAutoObservable(this);
  }

  setImgUrl = (url: string) => {
    this.imgUrl = url;
  }

  setPrompt = (p: string) => {
    this.prompt = p.replace(/\s+/g, ' ').trim();
  }

  setSize = (type: 'outer' | 'inner' | 'origin', key: string, value: number) => {
    this[`${type}Size`][key] = value;
  }

  calcSize = (isInit = false) => {
    const size = zoomImg({
      type: 'contain',
      width: this.originSize.wrapWidth,
      height: this.originSize.wrapHeight,
    }, {
      width: this.outerSize.realWidth,
      height: this.outerSize.realHeight,
    });
    const resizeRatio = size.width / this.outerSize.width;
    this.outerSize.width = size.width;
    this.outerSize.height = size.height;
    this.outerSize.left = size.left;
    this.outerSize.top = size.top;
    const ratio = isInit ? .8 : 1;
    const innerSize = zoomImg({
      type: 'contain',
      width: size.width * ratio,
      height: size.height * ratio,
    }, {
      width: this.originSize.width,
      height: this.originSize.height,
    });

    if (isInit) {
      this.innerSize.width = innerSize.width;
      this.innerSize.height = innerSize.height;
      this.innerSize.left = innerSize.left + size.width * .1;
      this.innerSize.top = innerSize.top + size.height * .1;
    } else {
      this.innerSize.width *= resizeRatio;
      this.innerSize.height *= resizeRatio;
      this.innerSize.left *= resizeRatio;
      this.innerSize.top *= resizeRatio;
    }
  }

  dragEnd = () => {
    const size = zoomImg({
      type: 'contain',
      width: this.originSize.wrapWidth,
      height: this.originSize.wrapHeight,
    }, {
      width: this.outerSize.realWidth,
      height: this.outerSize.realHeight,
    });
    const resizeRatio = size.width / this.outerSize.width;
    this.outerSize.width = size.width;
    this.outerSize.height = size.height;
    this.outerSize.left = size.left;
    this.outerSize.top = size.top;
    this.innerSize.left *= resizeRatio;
    this.innerSize.top *= resizeRatio;
    this.innerSize.width *= resizeRatio;
    this.innerSize.height *= resizeRatio;
  }

  calcRatio = () => {
    const ratio = this.innerSize.realWidth / this.innerSize.realHeight;
    if (ratio >= this.currentRatio) {
      this.outerSize.realWidth = this.innerSize.realWidth;
      this.outerSize.realHeight = this.innerSize.realWidth / this.currentRatio;
    } else {
      this.outerSize.realHeight = this.innerSize.realHeight;
      this.outerSize.realWidth = this.innerSize.realHeight * this.currentRatio;
    }
  }

  setRatio = (ratio: number) => {
    appLogger.log({
      key: "aibot_photo_edit",
      action: 'click',
      arrt: 'expand',
      type: 'ratio',
      ext1: ratio,
    });

    this.currentRatio = ratio;
    this.calcRatio();
    this.calcSize(true);
  }

  setWrapSize = (width: number, height: number) => {
    this.originSize.wrapWidth = width;
    this.originSize.wrapHeight = height;
    this.calcSize();
  }

  initSize = (width: number, height: number, wrapWidth: number, wrapHeight: number) => {
    this.originSize.width = width;
    this.originSize.height = height;
    this.innerSize.realWidth = width;
    this.innerSize.realHeight = height;
    this.originSize.wrapWidth = wrapWidth;
    this.originSize.wrapHeight = wrapHeight;
    this.calcRatio();
    this.calcSize(true);
  }
}

export default new Store();
